/**
 * Download a Video Studio job via the authenticated attachment endpoint.
 * Cross-origin S3 URLs ignore `<a download>` and open in a new tab instead.
 *
 * @param {number|string} jobId
 * @param {string} [preferredFilename] suggested name (e.g. project title); server Content-Disposition wins when present
 * @param {{ onProgress?: (info: { phase: string, percent: number|null, loaded?: number, total?: number|null }) => void }} [opts]
 */
export async function downloadVideoStudioJob(jobId, preferredFilename, opts = {}) {
  const id = Number(jobId);
  if (!Number.isFinite(id) || id < 1) {
    throw new Error("Missing video job id");
  }

  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
  onProgress?.({ phase: "preparing", percent: 0 });

  const token = localStorage.getItem(process.env.REACT_APP_TOKEN + "_user");
  const apiBase = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  const path = `/api/video-studio/jobs/${id}/download`;
  const url = apiBase ? `${apiBase}${path}` : path;

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const ct = (res.headers.get("Content-Type") || "").toLowerCase();

  if (ct.includes("application/json")) {
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = j.msg || j.message || (Array.isArray(j.message) ? j.message.join(", ") : msg);
    } catch {
      /* */
    }
    throw new Error(msg);
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const text = await res.text();
      if (text) {
        try {
          const j = JSON.parse(text);
          detail = j.msg || j.message || text.slice(0, 240);
        } catch {
          detail = text.slice(0, 240);
        }
      }
    } catch {
      /* */
    }
    throw new Error(detail);
  }

  const totalHeader = Number(res.headers.get("Content-Length"));
  const total = Number.isFinite(totalHeader) && totalHeader > 0 ? totalHeader : null;

  let blob;
  if (res.body && typeof res.body.getReader === "function") {
    const reader = res.body.getReader();
    const chunks = [];
    let loaded = 0;
    onProgress?.({ phase: "downloading", percent: total ? 0 : null, loaded: 0, total });

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      const percent = total ? Math.min(99, Math.round((loaded / total) * 100)) : null;
      onProgress?.({ phase: "downloading", percent, loaded, total });
    }
    blob = new Blob(chunks, { type: res.headers.get("Content-Type") || "video/mp4" });
  } else {
    onProgress?.({ phase: "downloading", percent: null, total });
    blob = await res.blob();
  }

  onProgress?.({ phase: "saving", percent: 100, loaded: blob.size, total: total || blob.size });

  let filename = sanitizeDownloadFilename(preferredFilename, `video-studio-${id}.mp4`);
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i);
    const raw = decodeURIComponent((m?.[1] || m?.[2] || m?.[3] || "").trim());
    if (raw) filename = sanitizeDownloadFilename(raw, filename);
  }

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);

  onProgress?.({ phase: "done", percent: 100 });
  return { filename, size: blob.size };
}

/** Build a safe .mp4 download name from a project/job title. */
export function videoJobDownloadFilename(job) {
  const title = String(job?.title || "").trim();
  const prompt = String(job?.prompt || "").trim();
  const base = title || prompt || `video-studio-${job?.id || "export"}`;
  return sanitizeDownloadFilename(base, `video-studio-${job?.id || "export"}.mp4`);
}

function sanitizeDownloadFilename(raw, fallback) {
  const fallbackSafe = String(fallback || "video.mp4").trim() || "video.mp4";
  let name = String(raw || "").trim();
  if (!name) return fallbackSafe.endsWith(".mp4") ? fallbackSafe : `${fallbackSafe}.mp4`;

  // Strip path segments if a full path slipped in.
  name = name.replace(/^.*[\\/]/, "");
  const hasMp4 = /\.mp4$/i.test(name);
  const withoutExt = name.replace(/\.[a-z0-9]{2,5}$/i, "");
  const safe =
    withoutExt
      .replace(/[^\w\-]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 120) || "video";
  return hasMp4 || fallbackSafe.endsWith(".mp4") ? `${safe}.mp4` : `${safe}.mp4`;
}
